import React, { useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

import { Icon } from './Icon';
import styles from './Typography.module.css';

// ── Shared types ─────────────────────────────────────────────────────────────

type TypographyType = 'default' | 'secondary' | 'success' | 'warning' | 'danger';

// ── CopyButton (internal) ────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={clsx(styles.copyButton, copied && styles.copyButtonCopied)}
      aria-label={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? <Icon name="check" size={14} /> : <Icon name="content_copy" size={14} />}
    </button>
  );
}

// ── Text CVA definition ───────────────────────────────────────────────────────

const text = cva(styles.textBase, {
  variants: {
    type: {
      default: styles.typeDefault,
      secondary: styles.typeSecondary,
      success: styles.typeSuccess,
      warning: styles.typeWarning,
      danger: styles.typeDanger,
    },
    strong: { true: styles.strong },
    italic: { true: styles.italic },
    underline: { true: styles.underline },
    del: { true: styles.del },
    code: { true: styles.code },
    size: {
      xs: styles.sizeXs,
      sm: styles.sizeSm,
      base: styles.sizeBase,
      md: styles.sizeMd,
      lg: styles.sizeLg,
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

// ── Text ─────────────────────────────────────────────────────────────────────

interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Semantic color type */
  type?: TypographyType;
  /** Bold text */
  strong?: boolean;
  /** Italic text */
  italic?: boolean;
  /** Underline text */
  underline?: boolean;
  /** Strikethrough text */
  del?: boolean;
  /** Inline code style */
  code?: boolean;
  /** Truncate with ellipsis */
  ellipsis?: boolean;
  /** Show copy button */
  copyable?: boolean;
  /** Override font size with design token key */
  size?: 'xs' | 'sm' | 'base' | 'md' | 'lg';
  children?: React.ReactNode;
}

/**
 * Typography.Text — inline text with semantic color, decoration, and copy support.
 *
 * Props: type, strong, italic, underline, del, code, ellipsis, copyable, size.
 *
 * @example
 * <Text type="success" strong>Saved!</Text>
 * <Text type="secondary" ellipsis>Very long text that gets truncated…</Text>
 * <Text code>const x = 1</Text>
 * <Text copyable>Copy this text</Text>
 */
function Text({
  type = 'default',
  strong,
  italic,
  underline,
  del,
  code,
  ellipsis,
  copyable,
  size,
  style,
  className,
  children,
  ...props
}: TextProps) {
  const textContent = typeof children === 'string' ? children : '';

  // Combined underline+del uses a compound class rather than two separate classes
  // (CSS text-decoration-line doesn't stack across separate class rules).
  const decorationClass = underline && del
    ? styles.underlineDel
    : underline
      ? styles.underline
      : del
        ? styles.del
        : undefined;

  // When code is set AND a size is given, the .code class sets font-size to 0.9em by default
  // but the size variant class overrides it. When code + no size, 0.9em from .code applies.
  const baseClassName = clsx(
    text({ type, strong, italic, code, size }),
    decorationClass,
    ellipsis && styles.ellipsis,
    ellipsis && styles.textEllipsis,
    className,
  );

  if (code) {
    return (
      <code className={baseClassName} spellCheck={false} style={style} {...(props as React.HTMLAttributes<HTMLElement>)}>
        {children}
        {copyable && textContent && <CopyButton text={textContent} />}
      </code>
    );
  }

  return (
    <span className={baseClassName} style={style} {...props}>
      {children}
      {copyable && textContent && <CopyButton text={textContent} />}
    </span>
  );
}

// ── Title CVA definition ──────────────────────────────────────────────────────

const title = cva(styles.titleBase, {
  variants: {
    level: {
      1: styles.level1,
      2: styles.level2,
      3: styles.level3,
      4: styles.level4,
      5: styles.level5,
    },
    type: {
      default: styles.typeDefault,
      secondary: styles.typeSecondary,
      success: styles.typeSuccess,
      warning: styles.typeWarning,
      danger: styles.typeDanger,
    },
  },
  defaultVariants: {
    level: 1,
    type: 'default',
  },
});

// ── Title ────────────────────────────────────────────────────────────────────

interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level 1–5 */
  level?: 1 | 2 | 3 | 4 | 5;
  /** Semantic color type */
  type?: TypographyType;
  /** Truncate with ellipsis */
  ellipsis?: boolean;
  children?: React.ReactNode;
}

/**
 * Typography.Title — heading component (h1–h5) with semantic color.
 *
 * Props: level (1–5), type, ellipsis.
 *
 * Sizes scale down responsively. Margins use spacing tokens.
 *
 * @example
 * <Title level={1}>Page Title</Title>
 * <Title level={3} type="secondary">Section Header</Title>
 */
function Title({ level = 1, type = 'default', ellipsis, style, className, children, ...props }: TitleProps) {
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

  return (
    <Tag
      className={clsx(title({ level, type }), ellipsis && styles.ellipsis, className)}
      style={style}
      {...props}
    >
      {children}
    </Tag>
  );
}

// ── Paragraph CVA definition ──────────────────────────────────────────────────

const paragraph = cva(styles.paragraphBase, {
  variants: {
    type: {
      default: styles.typeDefault,
      secondary: styles.typeSecondary,
      success: styles.typeSuccess,
      warning: styles.typeWarning,
      danger: styles.typeDanger,
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

// ── Paragraph ────────────────────────────────────────────────────────────────

interface ParagraphProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Semantic color type */
  type?: TypographyType;
  /** Truncate: true for 1 line, or { rows: N } for multi-line clamp */
  ellipsis?: boolean | { rows: number };
  /** Show copy button */
  copyable?: boolean;
  children?: React.ReactNode;
}

/**
 * Typography.Paragraph — block-level text with optional line clamping.
 *
 * Props: type, ellipsis (boolean or { rows: N }), copyable.
 *
 * @example
 * <Paragraph>Regular paragraph text.</Paragraph>
 * <Paragraph ellipsis={{ rows: 3 }}>Long text clamped to 3 lines…</Paragraph>
 * <Paragraph copyable>Text that can be copied.</Paragraph>
 */
function Paragraph({ type = 'default', ellipsis, copyable, style, className, children, ...props }: ParagraphProps) {
  const textContent = typeof children === 'string' ? children : '';
  const rows = typeof ellipsis === 'object' ? ellipsis.rows : ellipsis ? 1 : 0;

  // WebkitLineClamp is runtime-computed from the rows prop (continuous range) —
  // it is set as an inline style because no finite class set can express it.
  const clampStyle: React.CSSProperties = rows > 0
    ? { WebkitLineClamp: rows, ...style }
    : style ?? {};

  return (
    <p
      className={clsx(paragraph({ type }), rows > 0 && styles.paragraphClamp, className)}
      style={clampStyle}
      {...props}
    >
      {children}
      {copyable && textContent && <CopyButton text={textContent} />}
    </p>
  );
}

// ── Exports ──────────────────────────────────────────────────────────────────

export const Typography = {
  Text,
  Title,
  Paragraph,
};

export { Text, Title, Paragraph };
